import { useState, useEffect, useMemo, useCallback, useRef, type JSX } from 'react';
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import {
  Save,
  Send,
  Plus,
  Copy,
  Check,
  AlertTriangle,
  ShieldAlert,
  ChevronDown,
} from 'lucide-react';
import clsx from 'clsx';
import { supabase } from '../../lib/supabase';
import { useApp } from '../../hooks/useApp';
import { useParamStore } from '../../hooks/useParamStore';
import {
  computeParamDefaults,
  DEFAULT_RISK_CONFIG,
  RISK_FACTOR_FIELDS,
} from '../../lib/buyingSessionDefaults';
import {
  resolvePoConstraints,
  resolveGuardrails,
  checkLineQty,
  maxSpendOverBy,
  aggregateByRisk,
  classifySkuRisk,
  evaluateGuardrails,
  type ResolvedPoConstraints,
  type RiskConfig,
  type RiskFactorKey,
  type GuardrailBreach,
} from '../../lib/poConstraints';
import type { BuyingSessionRiskPref } from '../../types';
import { usePoConcentration } from '../../hooks/usePoConcentration';
import {
  usePOStaging,
  type AddPOStagingResult,
  type UsePOStagingResult,
} from '../../hooks/usePOStaging';
import { usePOBuilder, type CreditInfo } from '../../hooks/usePOBuilder';
import { useSettingsConfig } from '../../hooks/useSettingsConfig';
import { triggerTicker } from '../../lib/activityTicker';
import ArbiSelect from '../../components/ui/ArbiSelect';
import ArbiDatePicker from '../../components/ui/ArbiDatePicker';
import DistributorSelector from '../../components/ui/DistributorSelector';
import POLineItemsGrid from '../../components/purchasing/POLineItemsGrid';
import POConcentrationView from '../../components/purchasing/POConcentrationView';
import { Toast, type ToastState } from '../../components/ui/Toast';
// Day 76 P5.1 Phase 9B — PATH_LABEL retired; labels derived from
// useSettingsConfig().activePaths via pathLabel helper below.
import type { PathProfit } from '../../types/purchasing';
import type {
  CalcProfitabilitySalePriceSource,
  CalcProfitabilityVelocitySource,
} from '../../lib/engineFlags';
import AddProductsModal, {
  type AddProductsPayload,
} from '../../components/purchasing/AddProductsModal';

function fmt$(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtPct(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return `${Number(v).toFixed(1)}%`;
}
// DATA_STANDARDS-compliant currency (thousands separator) for the Day 98
// max_spend advisory amounts — spend caps are large enough that the comma
// materially aids readability. (Existing fmt$ above predates this and omits
// the separator; left untouched to avoid re-baselining the whole summary.)
function fmtUsd(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return `$${Number(v).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** Spawn a CSS-only ripple at the click point of the target button. */
function spawnRipple(e: React.MouseEvent<HTMLButtonElement>): void {
  const btn = e.currentTarget;
  const rect = btn.getBoundingClientRect();
  const size = Math.max(rect.width, rect.height);
  const span = document.createElement('span');
  span.className = 'arbi-po-confirm-ripple';
  span.style.width = `${size}px`;
  span.style.height = `${size}px`;
  span.style.left = `${e.clientX - rect.left - size / 2}px`;
  span.style.top = `${e.clientY - rect.top - size / 2}px`;
  span.setAttribute('aria-hidden', 'true');
  span.addEventListener('animationend', () => span.remove(), { once: true });
  btn.appendChild(span);
}

// Engine COALESCE fallback for the per-channel PO Impact card when
// product_profitability.payout_days is null on every staged row for a path.
// MUST stay in sync with the resolve_param_numeric defaults in the engine —
// see supabase/migrations/20260422000001_wire_safety_buffer.sql:71-75 (and
// any subsequent migration that re-defines the *_payout_days defaults).
// If those defaults change at the engine level, update this map in the same
// PR — divergence here would silently misrepresent the cash-back date column
// for null-payout rows.
const DEFAULT_PAYOUT_DAYS: Record<string, number> = {
  amazon_fba: 14,
  amazon_fbm: 14,
  ebay: 30,
  walmart_self: 14,
  walmart_wfs: 14,
  shopify: 3,
  // R1-F13: dropship is a LIVE, PROD-active path. Without this key the cash-back
  // date silently fell back to the `?? 14` default (the constant's own comment
  // warns divergence "would silently misrepresent"). Engine default is 30
  // (resolve_param_numeric('dropship_payout_days', …) COALESCE fallback).
  dropship: 30,
};

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  pending_review: 'Pending Review',
  rejected: 'Rejected',
  approved: 'Approved',
  ordered: 'Ordered',
  awaiting_invoice: 'Awaiting Invoice',
  processing: 'Processing',
  invoice_received: 'Invoice Received',
  partially_received: 'Partially Received',
  received: 'Received',
  closed: 'Closed',
  superseded: 'Superseded',
  cancelled: 'Cancelled',
  discrepancy: 'Discrepancy',
};

// Toast component + ToastState type imported from src/components/ui/Toast.tsx
// (Day 81.5 P1 F3 consolidation — unified kind+3-state shape).

// ── Confirmation Modal ────────────────────────────────────────

// Day 98.1 — map the persisted risk prefs (snake_case thresholds) to the
// classifier's RiskConfig (camelCase). Local to POBuilder — the only consumer
// that feeds the pure classifier.
function toRiskConfig(p: BuyingSessionRiskPref): RiskConfig {
  return {
    factors: p.factors,
    thresholds: {
      velocityFloor: p.thresholds.velocity_floor,
      competitionCeiling: p.thresholds.competition_ceiling,
    },
  };
}

// Day 98.1 — RiskFactorKey → human label for the per-SKU "why flagged" tooltip.
const RISK_FACTOR_LABEL = new Map<RiskFactorKey, string>(
  RISK_FACTOR_FIELDS.map((f) => [f.key, f.label])
);

interface ConfirmModalProps {
  open: boolean;
  poNumber: string;
  totalSkus: number;
  totalUnits: number;
  totalCost: number;
  estProfit: number;
  /** Day 98 H.17 — resolved max_spend cap (NULL = no cap). Advisory only:
   *  an over-cap PO surfaces a warning here but still submits on confirm. */
  maxSpend?: number | null;
  onConfirm: () => void;
  onCancel: () => void;
  submitting: boolean;
  /** W4a (Phase Wellspring) — staged lines that are resolver-resolved but not yet
   *  match-reviewed (the Moment-2 gate set). When non-empty the modal BLOCKS Confirm
   *  & Submit and surfaces the verify-matches ack. unreviewedSkuList mirrors the
   *  server PO.UNREVIEWED_MATCH enumeration so the same set shows both ways. */
  unreviewedLines: { product_id: string; isg_sku: string | null }[];
  unreviewedSkuList: string;
  onAckMatches: () => void;
  acking: boolean;
}

function ConfirmModal({
  open,
  poNumber,
  totalSkus,
  totalUnits,
  totalCost,
  estProfit,
  maxSpend,
  onConfirm,
  onCancel,
  submitting,
  unreviewedLines,
  unreviewedSkuList,
  onAckMatches,
  acking,
}: ConfirmModalProps): JSX.Element | null {
  if (!open) return null;
  // H.17 — advisory only. Surfaces the breach; never gates the Submit button.
  const overBy = maxSpendOverBy(totalCost, maxSpend ?? null);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop as a real <button> sibling so the dialog panel needs no onClick. */}
      <button
        type="button"
        className="absolute inset-0 bg-black/60 cursor-default"
        onClick={onCancel}
        aria-label="Close dialog"
        tabIndex={-1}
      />
      <div
        className="relative bg-surface-2 border border-border rounded-xl w-[420px] shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-label="Submit Purchase Order"
      >
        <div className="px-5 py-4 border-b border-border-subtle">
          <h3 className="text-sm font-semibold text-ink-bright">Submit Purchase Order</h3>
        </div>
        <div className="px-5 py-4 space-y-3">
          <p className="text-xs text-ink-secondary">You are about to submit the following PO:</p>
          <div className="bg-surface-3 rounded-lg p-3 space-y-1.5">
            <div className="flex justify-between text-xs">
              <span className="text-ink-muted">PO Number</span>
              <span className="font-mono text-ink-bright">
                {poNumber || 'Pending — assigned at submit'}
              </span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-ink-muted">SKUs</span>
              <span className="font-mono text-ink-bright">{totalSkus}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-ink-muted">Total Units</span>
              <span className="font-mono text-ink-bright">{totalUnits}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-ink-muted">Total Cost</span>
              <span className="font-mono text-ink-bright">{fmt$(totalCost)}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-ink-muted">Est. Profit</span>
              <span className="font-mono text-ink-bright">{fmt$(estProfit)}</span>
            </div>
          </div>
          {overBy != null && (
            <div
              className="flex items-start gap-1.5 text-2xs text-warning bg-warning/10 border border-warning/30 rounded p-2"
              data-testid="confirm-maxspend-advisory"
            >
              <AlertTriangle size={12} className="flex-shrink-0 mt-px" />
              <span>
                This PO is {fmtUsd(overBy)} over your {fmtUsd(maxSpend)} spend cap.
              </span>
            </div>
          )}
          {/* W4a — Moment-2 verification gate. BLOCKING (unlike the advisory above): Confirm &
              Submit stays disabled until every resolver-resolved line is verified. */}
          {unreviewedLines.length > 0 && (
            <div
              className="flex flex-col gap-2 text-2xs text-warning bg-warning/10 border border-warning/40 rounded p-2.5"
              data-testid="confirm-unreviewed-gate"
            >
              <div className="flex items-start gap-1.5">
                <AlertTriangle size={12} className="flex-shrink-0 mt-px" />
                <span>
                  {unreviewedLines.length} line{unreviewedLines.length === 1 ? '' : 's'}{' '}
                  {unreviewedLines.length === 1 ? 'needs' : 'need'} match verification before
                  submit: <span className="font-mono text-ink-secondary">{unreviewedSkuList}</span>
                </span>
              </div>
              <button
                onClick={onAckMatches}
                disabled={acking}
                className="arbi-btn-primary text-2xs self-start px-2.5 py-1 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {acking ? 'Verifying…' : 'I verified these matches'}
              </button>
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-border-subtle flex justify-end gap-2">
          <button onClick={onCancel} className="arbi-btn-ghost text-xs" disabled={submitting}>
            Cancel
          </button>
          <button
            onClick={(e) => {
              spawnRipple(e);
              onConfirm();
            }}
            className="arbi-btn-primary text-xs relative overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={submitting || unreviewedLines.length > 0}
            title={unreviewedLines.length > 0 ? 'Verify the flagged matches first' : undefined}
          >
            {submitting ? 'Submitting…' : 'Confirm & Submit'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Narrowed view of usePOStaging.POStagingItem — POBuilder's logic assumes
// product_id / quantity / cost_per_unit are always set on staged items
// (AddProducts modal enforces non-null at modal boundary; po_line_items
// .product_id is FK NOT NULL at hydration boundary). Structurally aligned
// with POLineItemsGrid.POLineItemRow for the covariant items prop
// pass-through. Day 64.x removed the cast-era optional modifiers on id +
// fulfillment_path that no longer reflect runtime truth (id non-null per
// DB PK; fulfillment_path non-null per Day 60.5 canonicalization with
// 'amazon_fba' default at usePOStaging boundary).
interface POStagingItem {
  id: string;
  product_id: string;
  isg_sku: string | null;
  quantity: number | null;
  cost_per_unit: number | null;
  fulfillment_path: string | null;
  /** Per-path unit allocation map persisted on the staging row. NULL when
   *  the staging entry was created without per-path intent (legacy / multi-add
   *  from a single path). When present, seeds the per-line allocation
   *  editor directly so DetailPanel multi-path entries survive into PO Builder. */
  allocations?: Record<string, number> | null;
  products?: {
    // U10 A1 — junction-assembled at the usePOStaging fetch (primary
    // product_distributors row → cost_usd; slice-B drop family).
    cost_usd?: number | null;
    // W4a — verification-gate predicate inputs + the grid's ASIN / match-status columns.
    asin?: string | null;
    match_reviewed?: boolean | null;
    match_resolved_at?: string | null;
  } | null;
}

// (CreditInfo comes from hooks/usePOBuilder.)

type ProfitDataMap = Record<string, Record<string, PathProfit | undefined> | undefined>;

interface POTypeConfig {
  slug: string;
  name: string;
  show_volume_tier?: boolean | null;
  show_discount_pct?: boolean | null;
}

interface ExistingPO {
  id: string;
  po_number: string;
  status: string;
  distributor_id: string | null;
  order_date: string | null;
  expected_delivery_date: string | null;
  fulfillment_path: string | null;
}

interface ValidationErrors {
  items?: string;
  distributor?: string;
  orderDate?: string;
  /** Day 98 H.18/H.19 — aggregate per-line quantity-limit block message. */
  lines?: string;
}

// ── Main POBuilder ────────────────────────────────────────────

export default function POBuilder(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const { poId } = useParams();
  // Day 64.5 V-ROUI: viewer hides Save Draft, Submit PO, and Add Products
  // affordances. AddProductsModal + ConfirmModal mounts also gate so viewer
  // never sees the modal trees. POLineItemsGrid still renders read-only.
  // Open Day 64.x snake_case bug NOT affected (write paths gated, viewer
  // never reaches L485 hydration loop or L1000 handleAddProduct via UI).
  const { distributors, activeDistributor, isAdmin, isViewer, buyingSessionPrefs } = useApp();

  // Boundary narrowing — items are typed as POStagingItem (non-null product_id
  // + non-null fulfillment_path) per two runtime invariants:
  //   1. po_line_items.product_id is FK NOT NULL — hydration source always
  //      populates
  //   2. AddProductsModal builds payload with explicit productId: product.id
  //      — modal source always populates
  // If either invariant breaks (e.g. future schema migration drops the FK
  // constraint or a new staging entry path bypasses AddProductsModal), this
  // narrowing point is where the lie surfaces — much better than ~12 sites
  // silently propagating nulls deep into POBuilder's consumer tree. The
  // Day 64.x cast is structurally different from the pre-Day-64.x cast at
  // this site (which hid the addItem signature mismatch — fixed now): this
  // cast documents a true operational invariant; the previous one hid a bug.
  // Day 80 P5.5 F48 — localToast declared early so usePOStaging onError can
  // wire it. The useEffect bridge from builder.toast → localToast lives
  // below (intentionally — runs after usePOBuilder() declares builder).
  const [localToast, setLocalToast] = useState<ToastState | null>(null);
  const staging = usePOStaging({
    onError: (message) => setLocalToast({ kind: 'error', message }),
  }) as Omit<UsePOStagingResult, 'items'> & {
    items: POStagingItem[];
  };
  const paramStore = useParamStore();
  const { poTypes: livePoTypesRaw, activePaths } = useSettingsConfig();
  // Day 76 P5.1 Phase 9B — path label lookup replacing hardcoded PATH_LABEL.
  // Falls back to path_key for historical-data carve-out (deactivated paths).
  const pathLabel = useCallback(
    (path: string): string => activePaths.find((p) => p.path_key === path)?.short_label ?? path,
    [activePaths]
  );
  const livePoTypes = livePoTypesRaw as unknown as POTypeConfig[];
  const livePoTypeOptions = livePoTypes.map((t) => ({ value: t.slug, label: t.name }));
  // Day 75 (D4) — derived from useSettingsConfig().activePaths so dropship
  // (and any future paths) surface automatically when toggled is_active=true.
  // Preserves 'mixed' sentinel for multi-path POs.
  const fulfillmentOptions = useMemo(
    () => [
      ...activePaths.map((p) => ({ value: p.path_key, label: p.display_name })),
      { value: 'mixed', label: 'Mixed' },
    ],
    [activePaths]
  );
  const builder = usePOBuilder();
  const [editMode, setEditMode] = useState<boolean>(false);
  const [editPo, setEditPo] = useState<ExistingPO | null>(null);

  // ── PO details state ────────────────────────────────────────
  const [poNumber, setPoNumber] = useState<string>('');
  const [distributorId, setDistributorId] = useState<string>(activeDistributor?.id || '');

  // Day 98 (P8.5) Checkpoint A — hybrid constraint resolution. Live in-session
  // caps arrive via nav-state from POTray's "Build PO"; when absent (Dashboard /
  // PO-list / mid-build refresh) fall back to the user's saved
  // buying_session_prefs defaults. po_staging is session-agnostic (Decision 30),
  // so there is no session ui_state to read here — these two sources are the
  // authoritative path. Unset / non-positive value → no limit (skip).
  const navConstraints =
    (location.state as { constraints?: Record<string, string | undefined> } | null)?.constraints ??
    null;
  // Day 98.1b — live in-session guardrail caps (POTray "Build PO" nav-state),
  // resolved over saved prefs.guardrails the same way constraints are.
  const navGuardrails =
    (location.state as { guardrails?: Record<string, string | undefined> } | null)?.guardrails ??
    null;
  // Day 98.1 — extracted so the advisory guardrail layer resolves from the SAME
  // param-defaults map as resolvedConstraints. paramDefaults is the saved
  // prefs/params fallback (computeParamDefaults folds prefs.guardrails); the live
  // in-session nav-state leg is layered per-call via resolveGuardrails (at :862).
  const paramDefaults = useMemo(
    () => computeParamDefaults(paramStore, distributorId || null, buyingSessionPrefs),
    [paramStore, distributorId, buyingSessionPrefs]
  );
  const resolvedConstraints: ResolvedPoConstraints = useMemo(
    () => resolvePoConstraints(navConstraints, paramDefaults),
    [navConstraints, paramDefaults]
  );
  // Day 98.1 — risk config = saved prefs.risk, else the ratified default (3 live
  // factors on). prefs-only per the cleave; live in-session risk toggles land 98.1b.
  const resolvedRisk = useMemo<RiskConfig>(
    () => toRiskConfig(buyingSessionPrefs?.risk ?? DEFAULT_RISK_CONFIG),
    [buyingSessionPrefs]
  );
  const [orderDate, setOrderDate] = useState<string>(new Date().toISOString().slice(0, 10));
  const [expectedDelivery, setExpectedDelivery] = useState<string>('');
  const [fulfillmentPath, setFulfillmentPath] = useState<string>('amazon_fba');
  const [notes, setNotes] = useState<string>('');
  const [copied, setCopied] = useState<boolean>(false);
  const [poType, setPOType] = useState<string>('regular');
  const [volumeTier, setVolumeTier] = useState<string>('');
  const [discountPct, setDiscountPct] = useState<string>('');

  // Declared AFTER poType so selectedPoType doesn't reference a TDZ'd variable
  // (the .jsx version had this flipped; TS surfaced the bug at compile time).
  const selectedPoType = livePoTypes.find((t) => t.slug === poType);

  // ── Modals ──────────────────────────────────────────────────
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  // W4a — Moment 2 ack in-flight (the mark_match_reviewed loop fired from the submit-confirm modal).
  const [acking, setAcking] = useState<boolean>(false);
  // Day 56 item 2 — per-channel PO Impact card expand/collapse. Default
  // collapsed; state is ephemeral (not session_ui_state).
  const [perChannelExpanded, setPerChannelExpanded] = useState<boolean>(false);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});

  // ── Profitability data ──────────────────────────────────────
  const [profitData, setProfitData] = useState<ProfitDataMap>({});

  // ── Credit info ─────────────────────────────────────────────
  const [creditInfo, setCreditInfo] = useState<CreditInfo | null>(null);

  // ── W4a — PO-submit verification gate (Moment 2) ─────────────
  // A staged line is gate-requiring iff match_resolved_at IS NOT NULL AND match_reviewed = false —
  // the EXACT submit_purchase_order server predicate. Strict `=== false` mirrors SQL `= false`:
  // legacy match_resolved_at-NULL rows pass, reviewed rows pass, a NULL match_reviewed is not
  // gate-requiring. Both submit paths funnel through staging.items, so this covers new + edit mode.
  const unreviewedLines = useMemo(
    () =>
      staging.items
        .filter(
          (i) => i.products?.match_resolved_at != null && i.products?.match_reviewed === false
        )
        .map((i) => ({ product_id: i.product_id, isg_sku: i.isg_sku })),
    [staging.items]
  );
  // Client enumeration mirroring the server string_agg(DISTINCT pr.isg_sku, ', ' ORDER BY isg_sku):
  // the buyer sees the same full set in the modal pre-check AND the backstop userError. Fix once.
  const unreviewedSkuList = useMemo(
    () =>
      [...new Set(unreviewedLines.map((l) => l.isg_sku).filter((s): s is string => !!s))]
        .sort()
        .join(', '),
    [unreviewedLines]
  );

  // ── Manufacturer discount detection ─────────────────────────
  // After staging + distributor settle, walk each product → brand →
  // manufacturer, then check manufacturer_distributors for a discount_pct
  // on (manufacturer, active distributor). Groups are exposed via the
  // PO Summary card so the user can apply the discount with one click.
  interface MfrDiscountGroup {
    manufacturerId: string;
    manufacturerName: string;
    discountPct: number;
    stagingIds: string[]; // po_staging row ids to adjust on apply
    grossCost: number; // pre-discount cost across the group's staging rows
  }
  const [mfrDiscounts, setMfrDiscounts] = useState<MfrDiscountGroup[]>([]);
  const [mfrDiscountApplying, setMfrDiscountApplying] = useState<string | null>(null);

  // ── Per-path allocations ───────────────────────────────────
  // Keyed by staging item id → { [path]: quantity }. Seeded from each
  // staging item's primary fulfillment_path at the row's full quantity; the
  // user can then redistribute inline in the line-items grid. Only non-zero
  // paths are kept in the map so the summary render stays clean.
  const [allocations, setAllocations] = useState<Record<string, Record<string, number>>>({});

  // Seed allocations for newly-loaded staging items, and drop entries for
  // removed items. Does not touch existing allocations — user edits survive
  // staging reloads from realtime or refetch.
  //
  // Seed priority:
  //   1. If staging.allocations is non-null and has at least one positive
  //      entry, use it directly. Preserves multi-path intent set in
  //      DetailPanel (B-R2-4 / B-R2-6).
  //   2. Otherwise fall back to the legacy single-path seed
  //      { [fulfillment_path]: quantity } so pre-feature staging rows and
  //      multi-add entries (which write a single-key map themselves) work
  //      identically to before.
  useEffect(() => {
    setAllocations((prev) => {
      const next: Record<string, Record<string, number>> = {};
      let changed = false;
      for (const it of staging.items) {
        if (!it.id) continue;
        const existing = prev[it.id];
        if (existing) {
          next[it.id] = existing;
        } else {
          const fromStaging = it.allocations || null;
          const hasFromStaging =
            fromStaging && Object.values(fromStaging).some((v) => Number(v) > 0);
          if (hasFromStaging) {
            // Coerce values to numbers — JSONB round-trips can produce strings
            // depending on driver settings; normalize here.
            const cleaned: Record<string, number> = {};
            for (const [k, v] of Object.entries(fromStaging)) {
              const n = Number(v);
              if (Number.isFinite(n) && n > 0) cleaned[k] = Math.floor(n);
            }
            next[it.id] = cleaned;
          } else {
            const path = it.fulfillment_path || 'amazon_fba';
            const qty = it.quantity && it.quantity > 0 ? it.quantity : 1;
            next[it.id] = { [path]: qty };
          }
          changed = true;
        }
      }
      // Also detect if any prev key disappeared — drop it.
      for (const k of Object.keys(prev)) {
        if (!(k in next)) {
          changed = true;
        }
      }
      if (!changed && Object.keys(next).length === Object.keys(prev).length) return prev;
      return next;
    });
  }, [staging.items]);

  const handleAllocationChange = useCallback(
    (stagingId: string, next: Record<string, number>): void => {
      // Strip zero/negative entries — the allocation map only holds "real" paths.
      const cleaned: Record<string, number> = {};
      for (const [path, qty] of Object.entries(next)) {
        const n = Math.max(0, Math.floor(Number(qty) || 0));
        if (n > 0) cleaned[path] = n;
      }
      setAllocations((prev) => ({ ...prev, [stagingId]: cleaned }));
    },
    []
  );

  const handleApplyManufacturerDiscount = useCallback(
    async (group: MfrDiscountGroup): Promise<void> => {
      setMfrDiscountApplying(group.manufacturerId);
      try {
        const factor = 1 - group.discountPct / 100;
        for (const stagingId of group.stagingIds) {
          const item = staging.items.find((i) => i.id === stagingId);
          if (!item) continue;
          const currentUnitCost =
            Number(item.cost_per_unit) || Number(item.products?.cost_usd) || 0;
          if (!currentUnitCost) continue;
          const newUnitCost = Math.round(currentUnitCost * factor * 100) / 100;
          await staging.updateCost(stagingId, newUnitCost);
        }
      } finally {
        setMfrDiscountApplying(null);
      }
    },
    [staging]
  );

  // Edit mode: load existing PO data
  useEffect(() => {
    if (!poId) return;
    async function loadExistingPO(): Promise<void> {
      const { data: po, error } = await supabase
        .from('purchase_orders')
        .select('*')
        .eq('id', poId!)
        .single();
      if (error || !po) {
        navigate('/purchasing/purchase-orders');
        return;
      }
      const poRow = po as unknown as ExistingPO;
      if (!['draft', 'rejected'].includes(poRow.status)) {
        navigate(`/purchasing/purchase-orders/${poId}`);
        return;
      }
      setEditMode(true);
      setEditPo(poRow);
      setPoNumber(poRow.po_number);
      setDistributorId(poRow.distributor_id || '');
      setOrderDate(poRow.order_date || new Date().toISOString().slice(0, 10));
      setExpectedDelivery(poRow.expected_delivery_date || '');
      setFulfillmentPath(poRow.fulfillment_path || 'amazon_fba');
      // Day 53e: notes load removed. purchase_orders.notes was dropped Day
      // 48.5; past notes now live in entity_notes and surface via NotesPanel
      // on PO Detail. Edit-mode textarea starts empty — new content appends
      // a fresh entity_note on save (audit-trail semantics).

      const { data: items } = await supabase
        .from('po_line_items')
        // U10 cost re-point — cost_usd allowlisted OUT of the products embed
        // (slice-B drop family): hydration consumes the line's own cost_per_unit
        // only; the embedded products cost was dead weight.
        .select('*, products:product_id(id, isg_sku, title, brand)')
        .eq('po_id', poId!);
      if (items?.length) {
        const typedItems = items as unknown as Array<{
          product_id: string;
          isg_sku: string | null;
          qty_ordered: number | null;
          cost_per_unit: number | null;
          fulfillment_path: string | null;
        }>;
        // Day 81 P5.6 (F53) — gate hydration on activePaths + the loaded
        // PO's distributor_id. activePaths=[] means Settings has every
        // path deactivated and the hydration fallback chain has no safe
        // landing. poRow.distributor_id is required (FK NOT NULL on
        // purchase_orders.distributor_id; defensive check).
        const hydrationDistributorId = poRow.distributor_id;
        if (activePaths.length === 0 || !hydrationDistributorId) {
          setLocalToast({
            kind: 'error',
            message:
              activePaths.length === 0
                ? 'No active fulfillment paths — enable one in Settings'
                : 'PO is missing distributor — hydration aborted',
          });
          return;
        }
        const fallbackPath = activePaths[0].path_key;
        // Day 64.x — converted snake_case → camelCase args per
        // AddPOStagingArgs contract. Aggregate per-call results so partial
        // hydration failure (rare — FK NOT NULL on product_id makes
        // individual addItem failures unlikely) surfaces as a single
        // error-tone toast instead of silently leaving the tray short.
        // Full success is silent — populated staging tray is the success
        // indicator. Mirrors Day 64 Phase 5.1 multi-add aggregation
        // pattern (inline since count is small and context is hydration,
        // not user-triggered multi-add).
        //
        // Day 81 P5.6 (F53) — replaced redundant `?? 'amazon_fba'` caller
        // fallback with `?? fallbackPath` from activePaths (guaranteed
        // non-empty by the gate above). distributorId threaded from the
        // PO row's distributor_id (Phase 0.5 disk-verified source).
        const results: AddPOStagingResult[] = [];
        for (const item of typedItems) {
          const r = await staging.addItem({
            productId: item.product_id,
            isgSku: item.isg_sku,
            costPerUnit: item.cost_per_unit ?? 0,
            quantity: item.qty_ordered ?? 1,
            distributorId: hydrationDistributorId,
            fulfillmentPath: item.fulfillment_path ?? fallbackPath,
          });
          results.push(r);
          if (!r.success) {
            console.error('[POBuilder] hydration addItem failed:', r.productId, r.error);
          }
        }
        const failed = results.filter((r) => !r.success).length;
        if (failed > 0) {
          setLocalToast({
            kind: 'error',
            message: `Hydrated ${results.length - failed} of ${results.length} items; ${failed} failed`,
          });
        }
      }
    }
    loadExistingPO();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poId]);

  // Reserve the PO number on mount (new PO only). generate_po_number RESERVES —
  // every call consumes a counter value — so this effect must fire at most once
  // per builder instance (the ref guards StrictMode/remount double-invoke; two
  // racing reservations are how the display and the committed number diverged
  // under degraded fetches — U10b S-C3). The functional '' → value transition
  // means a late resolution can never repaint a number that already settled or
  // was reserved at submit time: what the user sees is what commits, or the
  // display stays pending and submit reserves server-side at click.
  const poNumberReserved = useRef(false);
  useEffect(() => {
    if (poId || poNumberReserved.current) return;
    poNumberReserved.current = true;
    builder.generatePONumber().then((num) => {
      if (num) setPoNumber((prev) => prev || num);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poId]);

  // Set distributor when active changes (new PO only)
  useEffect(() => {
    if (poId) return;
    if (activeDistributor?.id) setDistributorId(activeDistributor.id);
  }, [activeDistributor, poId]);

  // Fetch profitability for staged products
  useEffect(() => {
    if (staging.items.length === 0) return;
    const productIds = staging.items.map((item) => item.product_id);
    const distId = distributorId || activeDistributor?.id;
    if (!distId) return;

    // Day 58 — payout_days dropped from SELECT projection (Day 56 latent bug:
    // product_profitability has NO payout_days column; the live engine writes
    // payout_days into days_credit_locked_safety as dts + payout_days, not
    // as a standalone column). The per-channel PO Impact card now relies
    // exclusively on the DEFAULT_PAYOUT_DAYS constant (engine COALESCE
    // mirror). Surfaced by Day 58 Layer 1 audit RUNTIME_FAIL on PO-BUILDER.
    supabase
      .from('product_profitability')
      .select(
        'product_id, marketplace, profit_per_unit, roi_pct, sale_price, total_cost_per_unit, sale_price_source, velocity_source'
      )
      .eq('distributor_id', distId)
      .in('product_id', productIds)
      .then(({ data }) => {
        if (!data) return;
        const map: ProfitDataMap = {};
        for (const row of data as unknown as Array<{
          product_id: string;
          /** pp's stored column (slice-B family) — value space is fpc path keys;
           *  keys the per-path ProfitDataMap below. */
          marketplace: string;
          profit_per_unit: number | null;
          roi_pct: number | null;
          sale_price: number | null;
          total_cost_per_unit: number | null;
          sale_price_source: CalcProfitabilitySalePriceSource | null;
          velocity_source: CalcProfitabilityVelocitySource | null;
        }>) {
          if (!map[row.product_id]) map[row.product_id] = {};
          map[row.product_id]![row.marketplace] = row;
        }
        setProfitData(map);
      });
  }, [staging.items, distributorId, activeDistributor]);

  // Fetch credit info
  useEffect(() => {
    const distId = distributorId || activeDistributor?.id;
    if (!distId) return;
    builder.getCreditInfo(distId).then((info) => setCreditInfo(info));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [distributorId, activeDistributor]);

  // Manufacturer discount detection — runs whenever the staging set or the
  // active distributor changes. Walks products → brand → manufacturer for
  // each staged product, then reads manufacturer_distributors for a discount
  // on the pair. Quietly clears when there's nothing to show.
  useEffect(() => {
    const distId = distributorId || activeDistributor?.id;
    if (!distId || staging.items.length === 0) {
      setMfrDiscounts([]);
      return;
    }
    const productIds = staging.items.map((i) => i.product_id).filter((id): id is string => !!id);
    if (productIds.length === 0) {
      setMfrDiscounts([]);
      return;
    }
    let cancelled = false;
    (async () => {
      // 1. Fetch products → brand_id + brand (text). `brand_id` is populated
      //    when the catalog has been linked to the brands table; otherwise
      //    we fall back to matching on the raw brand name.
      const { data: prodRows } = await supabase
        .from('products')
        .select('id, brand, brand_id')
        .in('id', productIds);
      const products =
        (prodRows as unknown as Array<{
          id: string;
          brand: string | null;
          brand_id: string | null;
        }> | null) || [];

      // 2. Fetch brands → manufacturer_id. Join by either brand_id (FK) or
      //    by brand name (the Algopix-imported fallback).
      const { data: brandRows } = await supabase.from('brands').select('id, name, manufacturer_id');
      const brands =
        (brandRows as unknown as Array<{
          id: string;
          name: string | null;
          manufacturer_id: string | null;
        }> | null) || [];
      const brandById = new Map(brands.map((b) => [b.id, b]));
      const brandByName = new Map(brands.filter((b) => b.name).map((b) => [b.name as string, b]));

      // 3. Build product_id → manufacturer_id map.
      const productToMfr: Record<string, string> = {};
      for (const p of products) {
        const viaId = p.brand_id ? brandById.get(p.brand_id) : undefined;
        const viaName = p.brand ? brandByName.get(p.brand) : undefined;
        const mfrId = viaId?.manufacturer_id || viaName?.manufacturer_id || null;
        if (mfrId) productToMfr[p.id] = mfrId;
      }

      const uniqueMfrIds = Array.from(new Set(Object.values(productToMfr)));
      if (uniqueMfrIds.length === 0) {
        if (!cancelled) setMfrDiscounts([]);
        return;
      }

      // 4. Fetch manufacturer names + the discount_pct for this distributor.
      const [{ data: mfrRows }, { data: mdRows }] = await Promise.all([
        supabase.from('manufacturers').select('id, name').in('id', uniqueMfrIds),
        supabase
          .from('manufacturer_distributors')
          .select('manufacturer_id, discount_pct')
          .eq('distributor_id', distId)
          .in('manufacturer_id', uniqueMfrIds),
      ]);
      const mfrNameById = new Map(
        ((mfrRows as unknown as Array<{ id: string; name: string }>) || []).map((m) => [
          m.id,
          m.name,
        ])
      );
      const discountByMfr = new Map<string, number>();
      for (const row of (mdRows as unknown as Array<{
        manufacturer_id: string;
        discount_pct: number | string;
      }>) || []) {
        const pct = Number(row.discount_pct);
        if (Number.isFinite(pct) && pct > 0) {
          discountByMfr.set(row.manufacturer_id, pct);
        }
      }

      // 5. Group staging ids under each manufacturer with an active discount.
      const groups: Record<string, MfrDiscountGroup> = {};
      for (const item of staging.items) {
        const pid = item.product_id;
        const mfrId = pid ? productToMfr[pid] : undefined;
        if (!mfrId) continue;
        const pct = discountByMfr.get(mfrId);
        if (pct === undefined) continue;
        const unitCost = Number(item.cost_per_unit) || Number(item.products?.cost_usd) || 0;
        const lineCost = unitCost * (item.quantity || 0);
        if (!groups[mfrId]) {
          groups[mfrId] = {
            manufacturerId: mfrId,
            manufacturerName: mfrNameById.get(mfrId) || 'Manufacturer',
            discountPct: pct,
            stagingIds: [],
            grossCost: 0,
          };
        }
        if (item.id) groups[mfrId].stagingIds.push(item.id);
        groups[mfrId].grossCost += lineCost;
      }
      if (!cancelled) setMfrDiscounts(Object.values(groups));
    })();
    return () => {
      cancelled = true;
    };
  }, [staging.items, distributorId, activeDistributor]);

  // ── Computed ────────────────────────────────────────────────

  // Day 98 (P8.5) — concentration view data. Reads the server-side po_staging
  // tray via calculate_po_concentration; refetches when staged rows /
  // quantities / costs / paths change. Not fetched in edit mode (edit-mode
  // concentration would read po_line_items — Checkpoint B BACKLOG deferral).
  const concentrationSig = useMemo(
    () =>
      staging.items
        .map((i) => `${i.id}:${i.quantity}:${i.cost_per_unit}:${i.fulfillment_path}`)
        .join('|'),
    [staging.items]
  );
  const concentration = usePoConcentration(
    distributorId || null,
    concentrationSig,
    !editMode && staging.items.length > 0
  );

  // Day 98.1 — advisory risk/guardrail layer, derived CLIENT-SIDE from the
  // concentration data (poConstraints pure fns). Recomputes on data changes (the
  // concentrationSig refetch: qty/cost/path/items) AND on any risk-config change
  // (resolvedRisk) — no refetch. by_risk is product-level classification, NOT the
  // by_marketplace allocation boundary, so it never "finalizes at submit"; it
  // simply reflects staged (pre-submit) quantities like every axis. Breaches
  // resolve the live nav-state guardrail caps (POTray "Build PO") over the saved
  // prefs.guardrails fallback via resolveGuardrails; evaluateGuardrails skips any
  // unset limit, so an unconfigured tray still yields [].
  const { byRisk, skuRisk, breaches } = useMemo(() => {
    const d = concentration.data;
    if (!d)
      return {
        byRisk: undefined as ReturnType<typeof aggregateByRisk> | undefined,
        skuRisk: undefined as Record<string, { highRisk: boolean; factors: string[] }> | undefined,
        breaches: undefined as GuardrailBreach[] | undefined,
      };
    const sku: Record<string, { highRisk: boolean; factors: string[] }> = {};
    for (const row of d.by_sku) {
      if (!row.label) continue;
      const res = classifySkuRisk(row, resolvedRisk);
      sku[row.label] = {
        highRisk: res.highRisk,
        factors: res.factors.map((k) => RISK_FACTOR_LABEL.get(k) ?? k),
      };
    }
    return {
      byRisk: aggregateByRisk(d.by_sku, resolvedRisk),
      skuRisk: sku,
      breaches: evaluateGuardrails(
        d,
        resolveGuardrails(navGuardrails, paramDefaults),
        resolvedRisk
      ),
    };
  }, [concentration.data, resolvedRisk, paramDefaults, navGuardrails]);

  // Per-channel PO Impact — units / profit / cash-back date per fulfillment
  // path. Iterates the user-edited allocations map; falls back to the staging
  // item's primary fulfillment_path × full qty when no allocation rows exist
  // for that line. Day 56 item 1+2.
  // FN-4 R5-F3: defined BEFORE `summary` so the summary's profit total is the
  // split-aware value this walk computes (was: a separate primary-path × full-qty
  // form that ignored allocations — wrong profit at the confirm-purchase moment).
  const perChannelImpact = useMemo(() => {
    type PerPath = {
      path: string;
      units: number;
      profit: number;
      payoutDays: number;
    };
    const acc: Record<string, PerPath> = {};
    for (const item of staging.items) {
      const itemId = item.id || '';
      const splitMap =
        (itemId && allocations[itemId]) ||
        ({
          [item.fulfillment_path || fulfillmentPath || 'amazon_fba']: item.quantity || 0,
        } as Record<string, number>);
      for (const [path, qty] of Object.entries(splitMap)) {
        const n = Number(qty) || 0;
        if (n <= 0) continue;
        const profEntry = profitData[item.product_id]?.[path];
        const profitPerUnit = Number(profEntry?.profit_per_unit) || 0;
        // payout_days comes exclusively from DEFAULT_PAYOUT_DAYS (engine
        // COALESCE mirror) — product_profitability does not carry the value
        // as a standalone column. See Day 58 SELECT projection comment above.
        const payoutDays = DEFAULT_PAYOUT_DAYS[path] ?? 14;
        if (!acc[path]) {
          acc[path] = { path, units: 0, profit: 0, payoutDays };
        }
        acc[path].units += n;
        acc[path].profit += profitPerUnit * n;
      }
    }
    const rows = Object.values(acc).sort((a, b) => b.profit - a.profit);
    return rows;
  }, [staging.items, allocations, profitData, fulfillmentPath]);

  const summary = useMemo(() => {
    const totalSkus = staging.items.length;
    const totalUnits = staging.items.reduce((s, item) => s + (item.quantity || 1), 0);
    const totalCost = staging.items.reduce((s, item) => {
      const cost = Number(item.cost_per_unit) || Number(item.products?.cost_usd) || 0;
      return s + cost * (item.quantity || 1);
    }, 0);
    // FN-4 R5-F3: profit total is the SPLIT-AWARE sum perChannelImpact already
    // computes (walks the per-line allocations map), not the primary-path × full-qty
    // form that overstated/understated profit when an operator split units across paths.
    const estTotalProfit = perChannelImpact.reduce((s, r) => s + r.profit, 0);
    const blendedROI = totalCost > 0 ? (estTotalProfit / totalCost) * 100 : 0;
    return { totalSkus, totalUnits, totalCost, estTotalProfit, blendedROI };
  }, [staging.items, perChannelImpact]);

  const creditImpactPct =
    creditInfo && creditInfo.creditLimit > 0
      ? (summary.totalCost / creditInfo.creditLimit) * 100
      : 0;
  const remainingCredit = (creditInfo?.available || 0) - summary.totalCost;
  // Day 98 H.17 — advisory breach amount, shared by the PO Summary line and
  // the ConfirmModal. NULL when there is no cap or the total is at/under it.
  const maxSpendOver = maxSpendOverBy(summary.totalCost, resolvedConstraints.maxSpend);
  const warningThreshold = Number(
    paramStore.resolveKey('credit_util_warning_pct')?.param.value ?? 50
  );
  const dangerThreshold = Number(
    paramStore.resolveKey('credit_util_danger_pct')?.param.value ?? 80
  );
  const availableDangerRatio =
    Number(paramStore.resolveKey('credit_available_danger_pct')?.param.value ?? 20) / 100;
  const creditColor =
    creditImpactPct < warningThreshold
      ? 'text-success'
      : creditImpactPct < dangerThreshold
        ? 'text-warning'
        : 'text-danger';
  const remainingColor =
    remainingCredit >= 0
      ? creditInfo && remainingCredit > creditInfo.creditLimit * availableDangerRatio
        ? 'text-success'
        : 'text-warning'
      : 'text-danger';

  // ── Copy PO number ──────────────────────────────────────────
  const copyPONumber = (): void => {
    navigator.clipboard.writeText(poNumber);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // ── Validation ──────────────────────────────────────────────
  const validate = (): boolean => {
    const errors: ValidationErrors = {};
    if (staging.items.length === 0) errors.items = 'At least one line item is required';
    if (!distributorId) errors.distributor = 'Select a distributor';
    if (!orderDate) errors.orderDate = 'Order date is required';
    // Day 98 H.18/H.19 — hard per-line quantity blocks. The per-cell error is
    // rendered live in POLineItemsGrid; this aggregate names the count and
    // gates submission. (max_spend H.17 is advisory — not checked here.)
    const violatingLines = staging.items.filter(
      (it) =>
        checkLineQty(
          it.quantity ?? 1,
          resolvedConstraints.maxUnitsPerSku,
          resolvedConstraints.minOrderQty
        ) !== null
    ).length;
    if (violatingLines > 0) {
      errors.lines = `${violatingLines} line item${
        violatingLines !== 1 ? 's' : ''
      } outside quantity limits — adjust to submit`;
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  interface BuiltLineItem {
    product_id: string;
    isg_sku: string | null;
    quantity: number;
    cost_per_unit: number;
    fulfillment_path: string;
    est_profit_per_unit: number | null;
    profitability_snapshot: PathProfit | null;
    /** Per-path split — persisted as po_line_item_allocations rows. */
    allocations: Record<string, number>;
  }

  /** Pick the path most representative of an allocation — the path carrying
   *  the largest quantity. Falls back to the staging row's own path so the
   *  deprecated po_line_items.fulfillment_path column still reflects the
   *  dominant channel. */
  function pickPrimaryPath(alloc: Record<string, number>, fallback: string): string {
    let best = fallback;
    let bestQty = -Infinity;
    for (const [path, qty] of Object.entries(alloc)) {
      if (qty > bestQty) {
        best = path;
        bestQty = qty;
      }
    }
    return best;
  }

  // ── Build line items for submission ─────────────────────────
  const buildLineItems = (): BuiltLineItem[] => {
    return staging.items.map((item) => {
      const stagingPath = item.fulfillment_path || fulfillmentPath || 'amazon_fba';
      const itemAlloc = (item.id && allocations[item.id]) || {};
      const hasAlloc = Object.values(itemAlloc).some((v) => v > 0);
      // If the user hasn't set allocations yet, fall back to the full qty on
      // the staging row's path. This keeps old PO flows (and any row that
      // never had its allocation popup opened) working end-to-end.
      const effectiveAlloc: Record<string, number> = hasAlloc
        ? itemAlloc
        : { [stagingPath]: item.quantity || 1 };
      const primaryPath = pickPrimaryPath(effectiveAlloc, stagingPath);
      const profEntry = profitData[item.product_id]?.[primaryPath];
      return {
        product_id: item.product_id,
        isg_sku: item.isg_sku,
        quantity: item.quantity || 1,
        cost_per_unit: Number(item.cost_per_unit) || Number(item.products?.cost_usd) || 0,
        fulfillment_path: primaryPath,
        est_profit_per_unit: profEntry?.profit_per_unit || null,
        profitability_snapshot: profEntry || null,
        allocations: effectiveAlloc,
      };
    });
  };

  // Per-path totals across all line items — renders in the PO Summary card.
  const pathTotals = useMemo<Array<{ path: string; units: number }>>(() => {
    const acc: Record<string, number> = {};
    for (const item of staging.items) {
      if (!item.id) continue;
      const alloc = allocations[item.id];
      if (alloc && Object.values(alloc).some((v) => v > 0)) {
        for (const [path, qty] of Object.entries(alloc)) {
          if (qty > 0) acc[path] = (acc[path] || 0) + qty;
        }
      } else {
        // Same fallback as buildLineItems — a row with no explicit allocation
        // implicitly puts all its units on the staging row's primary path.
        const path = item.fulfillment_path || fulfillmentPath || 'amazon_fba';
        acc[path] = (acc[path] || 0) + (item.quantity || 0);
      }
    }
    // Day 75 (D5) — iterate acc keys directly so deactivated paths still
    // surface their staged units; sort alphabetically for stable display.
    return Object.entries(acc)
      .filter(([, v]) => (v || 0) > 0)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([path, units]) => ({ path, units }));
  }, [staging.items, allocations, fulfillmentPath]);

  // ── Submit ──────────────────────────────────────────────────
  const handleSubmit = async (): Promise<void> => {
    if (!validate()) return;
    setShowConfirmModal(true);
  };

  // W4a — the "I verified these matches" ack (Moment 2). Marks each unreviewed line reviewed via
  // mark_match_reviewed, THEN refreshes staging so match_reviewed flips and the gate clears. The
  // RPCs must return ok before submit is re-enabled (no optimistic enable); on any failure we
  // surface it and leave the gate closed. A fuzzy row with no asin RAISES 22023 — the buyer must
  // confirm a candidate on the product page (Moment 1), not bulk-ack here.
  const handleAckMatches = async (): Promise<void> => {
    setAcking(true);
    try {
      for (const line of unreviewedLines) {
        const res = await builder.markMatchReviewed(line.product_id);
        if (!res.ok) {
          const sku = line.isg_sku ?? 'a line';
          const msg = res.error?.includes('has no asin')
            ? `${sku} has no confirmed Amazon match — open its product page and confirm a candidate first.`
            : `Couldn't verify ${sku}${res.error ? ` — ${res.error}` : ''}`;
          setLocalToast({ kind: 'error', message: msg });
          await staging.fetchItems(); // reflect any lines that DID flip before the failure
          return; // keep the gate closed
        }
      }
      await staging.fetchItems(); // match_reviewed now true → unreviewedLines empties → submit enables
      setLocalToast({ kind: 'success', message: 'Matches verified' });
    } finally {
      setAcking(false);
    }
  };

  const handleConfirmSubmit = async (): Promise<void> => {
    // W4a client backstop — never submit with unreviewed resolver matches. The modal's
    // Confirm & Submit is already disabled while any exist; this guard ALSO covers the edit-mode
    // path below, which UPDATEs purchase_orders directly and so bypasses submit_purchase_order's
    // PO.UNREVIEWED_MATCH server guard (that backstop only protects the new-PO RPC path at :submitPO).
    if (unreviewedLines.length > 0) {
      setLocalToast({
        kind: 'error',
        message: `These lines need match verification before submit: ${unreviewedSkuList}`,
      });
      return;
    }
    if (editMode && editPo) {
      // W4b-α (ADR-54 amendment) — the atomic resubmit RPC replaces the 4 non-atomic PostgREST calls
      // (UPDATE header → DELETE lines → INSERT lines → INSERT allocations) that bypassed submit's §4
      // gate. resubmit_purchase_order gates FIRST (PO.UNREVIEWED_MATCH), guards the from-state
      // (PO.NOT_EDITABLE), and writes header + delete-reinsert lines + nested allocations in ONE
      // transaction. Typed errors surface via builder.error (the error card below), exactly as the
      // new-PO submitPO branch does. The unreviewedLines guard at the top of handleConfirmSubmit stays
      // the UX belt; this RPC is the server suspenders.
      const result = await builder.resubmitPO({
        poId: poId!,
        distributorId,
        orderDate,
        expectedDeliveryDate: expectedDelivery || null,
        fulfillmentPath,
        lineItems: buildLineItems(),
      });
      if (!result.success) return;

      // Day 53d: route edit-mode notes to entity_notes per Day 48.5 design.
      // Non-fatal — PO update is already committed; failure here logs +
      // continues. Edit-mode is append-style (every save adds a new note);
      // overwrite-vs-append semantics decision deferred per BACKLOG item.
      if (notes && notes.trim().length > 0) {
        const { error: noteErr } = await (
          supabase.rpc as unknown as (
            name: string,
            args: Record<string, unknown>
          ) => PromiseLike<{ data: unknown; error: { message: string } | null }>
        )('create_entity_note', {
          p_entity_type: 'purchase_order',
          p_entity_id: poId!,
          p_text: notes.trim(),
          p_visibility: 'all_internal',
        });
        if (noteErr) {
          console.error('[POBuilder] create_entity_note failed:', noteErr.message);
        }
      }

      await staging.clearAll();
      setShowConfirmModal(false);
      navigate(`/purchasing/purchase-orders/${poId}`);
    } else {
      // The RPC inserts p_po_number verbatim — only a server-reserved number may
      // reach it. Pending preview (the mount reservation never resolved) →
      // reserve here; unreachable → refuse, never submit a guessed/empty number.
      let submitNumber = poNumber;
      if (!submitNumber) {
        submitNumber = (await builder.generatePONumber()) ?? '';
        if (!submitNumber) {
          setLocalToast({
            kind: 'error',
            message: 'PO number unavailable — check your connection and try again.',
          });
          return;
        }
        setPoNumber(submitNumber);
      }
      const result = await builder.submitPO({
        poNumber: submitNumber,
        distributorId,
        orderDate,
        expectedDeliveryDate: expectedDelivery || null,
        fulfillmentPath,
        notes,
        lineItems: buildLineItems(),
      });
      if (result.success) {
        await staging.clearAll();
        setShowConfirmModal(false);
        triggerTicker('PO submitted');
        navigate('/purchasing/purchase-orders');
      }
    }
  };

  // ── Save Draft ──────────────────────────────────────────────
  const handleSaveDraft = async (): Promise<void> => {
    if (staging.items.length === 0) {
      setValidationErrors({ items: 'At least one line item is required' });
      return;
    }
    // Same reserve-or-refuse guard as submit — save_po_draft also inserts
    // p_po_number verbatim.
    let draftNumber = poNumber;
    if (!draftNumber) {
      draftNumber = (await builder.generatePONumber()) ?? '';
      if (!draftNumber) {
        setLocalToast({
          kind: 'error',
          message: 'PO number unavailable — check your connection and try again.',
        });
        return;
      }
      setPoNumber(draftNumber);
    }
    await builder.saveDraft({
      poNumber: draftNumber,
      distributorId,
      orderDate,
      expectedDeliveryDate: expectedDelivery || null,
      fulfillmentPath,
      notes,
      lineItems: buildLineItems(),
    });
  };

  // ── Add product from modal ──────────────────────────────────
  // Day 64.x — retyped param from Partial<POStagingItem> (snake_case lie
  // hidden by the deleted L308 cast) to AddPOStagingArgs (camelCase truth).
  //
  // Day 81 P5.6 (F53) — modal payload is AddProductsPayload (4 fields:
  // productId, isgSku, costPerUnit, quantity); POBuilder supplements
  // distributorId + fulfillmentPath from its own state before calling
  // addItem so the modal stays minimal (search-and-pick) while the hook's
  // newly-required fields are satisfied. Gate on activePaths + distributor
  // mirrors Phase 5A/5B/5C; the modal trigger button itself is only
  // mounted for non-viewer roles so the gate's failure mode is rare
  // (settings race, not normal flow).
  const handleAddProduct = async (product: AddProductsPayload): Promise<void> => {
    if (activePaths.length === 0 || !distributorId) {
      setLocalToast({
        kind: 'error',
        message:
          activePaths.length === 0
            ? 'No active fulfillment paths — enable one in Settings'
            : 'Select a distributor before adding products',
      });
      return;
    }
    const result = await staging.addItem({
      ...product,
      distributorId,
      fulfillmentPath: fulfillmentPath || activePaths[0].path_key,
    });
    if (!result.success) {
      setLocalToast({
        kind: 'error',
        message: `Failed to add product to PO: ${result.error ?? 'unknown error'}`,
      });
    }
  };

  const existingProductIds = useMemo(
    () => new Set(staging.items.map((item) => item.product_id)),
    [staging.items]
  );

  // ── Local toast from builder ────────────────────────────────
  // Day 80 P5.5 F48 — localToast useState moved up next to usePOStaging
  // so onError callback can wire it. This effect remains the bridge from
  // builder.toast → localToast.
  useEffect(() => {
    if (builder.toast) {
      setLocalToast(builder.toast);
      builder.dismissToast();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [builder.toast]);

  if (staging.loading) {
    return (
      <div className="flex flex-col h-full -m-4">
        <div className="flex-1 flex items-center justify-center">
          <span className="text-ink-muted text-sm">Loading staged items…</span>
        </div>
      </div>
    );
  }

  // Volume tier + discount pct are currently captured via setters but not
  // persisted server-side; keep them rendered when selectedPoType flags it.
  void volumeTier;
  void discountPct;

  return (
    <div className="flex flex-col h-full -m-4">
      <Toast
        toast={localToast || builder.toast}
        onDismiss={() => {
          setLocalToast(null);
          builder.dismissToast();
        }}
      />

      {/* Fixed toolbar/header */}
      <div className="flex-shrink-0 px-4 py-2 bg-surface-1 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h1 className="text-base font-semibold text-ink-bright">
              {editMode ? `Edit PO ${poNumber}` : 'PO Builder'}
            </h1>
            <DistributorSelector />
            <span className="px-2 py-0.5 rounded text-2xs font-medium bg-surface-4 text-ink-muted">
              {STATUS_LABELS.draft}
            </span>
            {selectedPoType?.show_discount_pct && (
              <span className="flex items-center gap-1 px-2 py-0.5 rounded text-2xs font-medium bg-danger/10 text-danger border border-danger/20">
                <ShieldAlert size={10} /> No Distributor Returns
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {!isViewer && (
              <>
                <button
                  onClick={handleSaveDraft}
                  className="arbi-btn-ghost text-xs"
                  disabled={builder.submitting}
                >
                  <Save size={14} /> Save Draft
                </button>
                <button
                  onClick={handleSubmit}
                  className="arbi-btn-primary text-xs"
                  disabled={builder.submitting}
                >
                  <Send size={14} /> {editMode ? 'Save & Submit' : 'Submit PO'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Scrollable content area */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="space-y-4">
          {/* Section 1 — PO Details */}
          <div className="arbi-card p-4">
            <h2 className="text-sm font-semibold text-ink-bright mb-4">PO Details</h2>
            <div className="grid grid-cols-2 gap-4">
              {/* Left column */}
              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="po-number-display"
                    className="text-2xs text-ink-muted font-medium mb-1 block"
                  >
                    PO Number
                  </label>
                  <div className="flex items-center gap-2 max-w-xs">
                    <span
                      id="po-number-display"
                      className="arbi-input flex-1 font-mono text-xs bg-surface-3/50 select-all"
                    >
                      {poNumber || 'Pending — assigned at submit'}
                    </span>
                    <button
                      onClick={copyPONumber}
                      disabled={!poNumber}
                      className="p-1.5 text-ink-muted hover:text-ink-primary transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      aria-label="Copy PO number"
                    >
                      {copied ? <Check size={14} className="text-success" /> : <Copy size={14} />}
                    </button>
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="po-order-date"
                    className="text-2xs text-ink-muted font-medium mb-1 block"
                  >
                    Order Date
                  </label>
                  <div id="po-order-date" className="max-w-[160px]">
                    <ArbiDatePicker
                      value={orderDate || null}
                      onChange={(v) => setOrderDate(v ?? '')}
                    />
                  </div>
                  {validationErrors.orderDate && (
                    <p className="text-2xs text-danger mt-1">{validationErrors.orderDate}</p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="po-fulfillment-path"
                    className="text-2xs text-ink-muted font-medium mb-1 block"
                  >
                    Fulfillment Path
                  </label>
                  <div id="po-fulfillment-path" className="max-w-[200px]">
                    <ArbiSelect
                      value={fulfillmentPath}
                      onChange={setFulfillmentPath}
                      options={fulfillmentOptions}
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="po-type-select"
                    className="text-2xs text-ink-muted font-medium mb-1 block"
                  >
                    PO Type
                  </label>
                  <div id="po-type-select" className="max-w-[200px]">
                    <ArbiSelect
                      value={poType}
                      onChange={setPOType}
                      options={
                        livePoTypeOptions.length > 0
                          ? livePoTypeOptions
                          : [{ value: 'regular', label: 'Regular PO' }]
                      }
                    />
                  </div>
                </div>

                {selectedPoType?.show_volume_tier && (
                  <div>
                    <label
                      htmlFor="po-volume-tier"
                      className="text-2xs text-ink-muted font-medium mb-1 block"
                    >
                      Volume Tier
                    </label>
                    <input
                      id="po-volume-tier"
                      type="text"
                      value={volumeTier}
                      onChange={(e) => setVolumeTier(e.target.value)}
                      className="arbi-input text-xs max-w-xs"
                      placeholder="e.g. Tier 2 — 500+ units"
                    />
                  </div>
                )}

                {selectedPoType?.show_discount_pct && (
                  <div>
                    <label
                      htmlFor="po-discount-pct"
                      className="text-2xs text-ink-muted font-medium mb-1 block"
                    >
                      Discount %
                    </label>
                    <input
                      id="po-discount-pct"
                      type="text"
                      inputMode="decimal"
                      value={discountPct}
                      onChange={(e) => {
                        const v = e.target.value.replace(/[^0-9.]/g, '');
                        setDiscountPct(v);
                      }}
                      className="arbi-input text-xs max-w-[120px] font-mono"
                      placeholder="0.0"
                    />
                  </div>
                )}
              </div>

              {/* Right column */}
              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="po-distributor-select"
                    className="text-2xs text-ink-muted font-medium mb-1 block"
                  >
                    Distributor
                  </label>
                  <div id="po-distributor-select" className="max-w-xs">
                    <ArbiSelect
                      value={distributorId}
                      onChange={setDistributorId}
                      options={distributors.map((d) => ({ value: d.id, label: d.name }))}
                      placeholder="Select distributor…"
                    />
                  </div>
                  {validationErrors.distributor && (
                    <p className="text-2xs text-danger mt-1">{validationErrors.distributor}</p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="po-expected-delivery"
                    className="text-2xs text-ink-muted font-medium mb-1 block"
                  >
                    Expected Delivery
                  </label>
                  <div id="po-expected-delivery" className="max-w-[160px]">
                    <ArbiDatePicker
                      value={expectedDelivery || null}
                      onChange={(v) => setExpectedDelivery(v ?? '')}
                      min={orderDate || undefined}
                      clearable
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="po-notes"
                    className="text-2xs text-ink-muted font-medium mb-1 block"
                  >
                    Add a note
                  </label>
                  <textarea
                    id="po-notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="arbi-input w-full text-xs resize-none min-h-[100px]"
                    placeholder="Optional. Saved as a new entry in the PO's notes log."
                  />
                  {editMode && (
                    <p className="text-2xs text-ink-muted mt-1">
                      Past notes appear in the{' '}
                      <Link
                        to={`/purchasing/purchase-orders/${poId!}`}
                        className="text-accent hover:text-accent-hover underline"
                      >
                        PO detail page
                      </Link>
                      .
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Section 2 — Line Items */}
          <div className="arbi-card p-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-ink-bright">Line Items</h2>
              <div className="flex items-center gap-2">
                {validationErrors.items && (
                  <span className="text-2xs text-danger">{validationErrors.items}</span>
                )}
                {validationErrors.lines && (
                  <span className="text-2xs text-danger" data-testid="po-line-qty-block">
                    {validationErrors.lines}
                  </span>
                )}
                {!isViewer && (
                  <button
                    onClick={() => setShowAddModal(true)}
                    className="arbi-btn text-xs bg-accent/10 text-accent hover:bg-accent/20"
                  >
                    <Plus size={14} /> Add Products
                  </button>
                )}
              </div>
            </div>

            <POLineItemsGrid
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              items={staging.items}
              profitData={profitData}
              defaultFulfillmentPath={fulfillmentPath}
              allocations={allocations}
              onAllocationChange={handleAllocationChange}
              onUpdateQuantity={staging.updateQuantity}
              onUpdateCost={staging.updateCost}
              onUpdateFulfillmentPath={staging.updateFulfillmentPath}
              onRemove={staging.removeItem}
              maxUnitsPerSku={resolvedConstraints.maxUnitsPerSku}
              minOrderQty={resolvedConstraints.minOrderQty}
            />
          </div>

          {/* Error display */}
          {builder.error && (
            <div className="arbi-card p-4 border-danger/30 bg-danger/5">
              <div className="flex items-center gap-2 text-danger text-xs">
                <AlertTriangle size={14} />
                <span>{builder.error}</span>
              </div>
            </div>
          )}

          {/* Concentration (Day 98 P8.5) — spend distribution review, spec § 19 step 4.
              Advisory (spec § 10). New-PO only; edit-mode deferred (Checkpoint B). */}
          {!editMode && staging.items.length > 0 && (
            <POConcentrationView
              data={concentration.data}
              loading={concentration.loading}
              error={concentration.error}
              byRisk={byRisk}
              skuRisk={skuRisk}
              breaches={breaches}
            />
          )}

          {/* Summary + Credit Impact */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="arbi-card p-4">
              <h2 className="text-sm font-semibold text-ink-bright mb-4">PO Summary</h2>
              <div className="space-y-3">
                <div className="flex justify-between text-xs">
                  <span className="text-ink-muted">Total SKUs</span>
                  <span className="font-mono text-ink-bright">{summary.totalSkus}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-ink-muted">Total Units</span>
                  <span className="font-mono text-ink-bright">{summary.totalUnits}</span>
                </div>
                {pathTotals.length > 0 && (
                  <div className="pt-1 space-y-1">
                    <div className="text-2xs uppercase tracking-wider text-ink-muted">
                      Units per Path
                    </div>
                    {pathTotals.map(({ path, units }) => (
                      <div key={path} className="flex justify-between text-xs pl-2">
                        <span className="text-ink-muted">{pathLabel(path)}</span>
                        <span className="font-mono text-ink-primary">{units}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="border-t border-border-subtle my-2" />
                <div className="flex justify-between text-xs">
                  <span className="text-ink-muted">Total Cost</span>
                  <span className="font-mono text-ink-bright">{fmt$(summary.totalCost)}</span>
                </div>
                {maxSpendOver != null && (
                  <div
                    className="flex items-center justify-between text-2xs"
                    data-testid="po-summary-maxspend-breach"
                  >
                    <span className="flex items-center gap-1 text-warning">
                      <AlertTriangle size={11} /> Over spend cap
                    </span>
                    <span className="font-mono text-warning">
                      {fmtUsd(maxSpendOver)} over {fmtUsd(resolvedConstraints.maxSpend)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between text-xs">
                  <span className="text-ink-muted">Est. Total Profit</span>
                  <span
                    className={clsx(
                      'font-mono',
                      summary.estTotalProfit > 0 ? 'text-success' : 'text-danger'
                    )}
                  >
                    {fmt$(summary.estTotalProfit)}
                  </span>
                </div>
                <div className="flex justify-between text-xs">
                  <span
                    className="text-ink-muted"
                    title="Outlay-return ROI — engine profit ÷ raw outlay (what you actually pay). Distinct from the buying grid's ROI% (landed), which divides by fully-loaded landed cost."
                  >
                    Est. Blended ROI (outlay)
                  </span>
                  <span
                    className={clsx(
                      'font-mono',
                      summary.blendedROI > 0 ? 'text-success' : 'text-danger'
                    )}
                  >
                    {fmtPct(summary.blendedROI)}
                  </span>
                </div>
                <div className="border-t border-border-subtle my-2" />
                <div className="flex justify-between text-xs">
                  <span className="text-ink-muted">Credit Impact</span>
                  <span className={clsx('font-mono', creditColor)}>
                    {fmtPct(creditImpactPct)} of limit
                  </span>
                </div>

                {/* Manufacturer discount notices — one per manufacturer that
                    has a discount on this distributor. Apply is informational
                    + manual for V1 (per spec): admin clicks Apply and the
                    cost_per_unit on every staged row for that manufacturer is
                    scaled by (1 − discount_pct/100). */}
                {mfrDiscounts.length > 0 && (
                  <div className="pt-3 mt-1 border-t border-border-subtle space-y-2">
                    <div className="text-2xs uppercase tracking-wider text-ink-muted">
                      Manufacturer Discounts Available
                    </div>
                    {mfrDiscounts.map((group) => {
                      const savings = (group.grossCost * group.discountPct) / 100;
                      const busy = mfrDiscountApplying === group.manufacturerId;
                      return (
                        <div
                          key={group.manufacturerId}
                          className="bg-accent/10 border border-accent/30 rounded-lg p-2.5 space-y-1.5"
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="min-w-0">
                              <div className="text-xs font-semibold text-ink-bright truncate">
                                {group.manufacturerName}
                              </div>
                              <div className="text-2xs text-ink-muted">
                                {group.stagingIds.length} SKU
                                {group.stagingIds.length !== 1 ? 's' : ''} ·{' '}
                                <span className="font-mono">
                                  {Number(group.discountPct).toFixed(2)}% off
                                </span>{' '}
                                · Est. savings{' '}
                                <span className="font-mono text-success">{fmt$(savings)}</span>
                              </div>
                            </div>
                            {isAdmin && (
                              <button
                                onClick={() => handleApplyManufacturerDiscount(group)}
                                disabled={busy}
                                className="arbi-btn text-2xs bg-accent text-white hover:bg-accent-hover px-2 py-0.5 flex-shrink-0"
                              >
                                {busy ? 'Applying...' : 'Apply Discount'}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="arbi-card p-4">
              <h2 className="text-sm font-semibold text-ink-bright mb-4">Credit Impact</h2>
              {creditInfo ? (
                <div className="space-y-3">
                  <div className="flex justify-between text-xs">
                    <span className="text-ink-muted">Credit Available</span>
                    <span
                      className={clsx(
                        'font-mono',
                        creditInfo.available < 0 ? 'text-danger' : 'text-ink-bright'
                      )}
                    >
                      {fmt$(creditInfo.available)}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-ink-muted">This PO Cost</span>
                    <span className="font-mono text-ink-bright">{fmt$(summary.totalCost)}</span>
                  </div>
                  <div className="border-t border-border-subtle my-2" />
                  <div className="flex justify-between text-xs">
                    <span className="text-ink-muted">Remaining After</span>
                    <span className={clsx('font-mono font-medium', remainingColor)}>
                      {fmt$(remainingCredit)}
                    </span>
                  </div>
                  {remainingCredit < 0 && (
                    <div className="mt-2 px-3 py-2 rounded-lg bg-danger/10 border border-danger/20">
                      <div className="flex items-center gap-2 text-danger text-2xs">
                        <AlertTriangle size={12} />
                        <span>This PO exceeds the available credit limit</span>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-xs text-ink-muted">Loading credit info…</p>
              )}
            </div>
          </div>

          {/* Per-channel PO Impact — admin-only, hidden when staging is empty.
              Day 56 items 1+2: detail card + expand/collapse. */}
          {isAdmin && perChannelImpact.length > 0 && (
            <div className="arbi-card p-4">
              <button
                type="button"
                onClick={() => setPerChannelExpanded((v) => !v)}
                className="w-full flex items-center justify-between"
              >
                <h2 className="text-sm font-semibold text-ink-bright">Per-Channel PO Impact</h2>
                <ChevronDown
                  size={14}
                  className={clsx(
                    'text-ink-muted transition-transform',
                    perChannelExpanded && 'rotate-180'
                  )}
                />
              </button>

              {perChannelExpanded && (
                <div className="mt-4 space-y-2">
                  {/* Per-channel header row */}
                  <div className="grid grid-cols-[1fr_repeat(3,minmax(0,90px))] gap-3 pb-2 border-b border-border-subtle text-2xs uppercase tracking-wider text-ink-muted">
                    <span>Channel</span>
                    <span className="text-right">Units</span>
                    <span className="text-right">Est. Profit</span>
                    <span className="text-right">Cash Back</span>
                  </div>
                  {perChannelImpact.map((row) => {
                    const cashBack = new Date();
                    cashBack.setDate(cashBack.getDate() + row.payoutDays);
                    return (
                      <div
                        key={row.path}
                        className="grid grid-cols-[1fr_repeat(3,minmax(0,90px))] gap-3 text-xs items-baseline"
                      >
                        <span className="text-ink-primary">{pathLabel(row.path)}</span>
                        <span className="text-right font-mono text-ink-primary">{row.units}</span>
                        <span
                          className={clsx(
                            'text-right font-mono',
                            row.profit > 0 ? 'text-success' : 'text-danger'
                          )}
                        >
                          {fmt$(row.profit)}
                        </span>
                        <span className="text-right">
                          <span className="block font-mono text-ink-primary">
                            in {row.payoutDays}d
                          </span>
                          <span className="block font-mono text-2xs text-ink-muted">
                            {cashBack.toLocaleDateString()}
                          </span>
                        </span>
                      </div>
                    );
                  })}

                  {/* Credit draw breakdown */}
                  {creditInfo && (
                    <div className="pt-3 mt-3 border-t border-border-subtle space-y-1.5">
                      <div className="text-2xs uppercase tracking-wider text-ink-muted mb-1">
                        Credit Draw
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-ink-muted">PO Total</span>
                        <span className="font-mono text-ink-primary">
                          {fmt$(summary.totalCost)}
                        </span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-ink-muted">Distributor Credit Limit</span>
                        <span className="font-mono text-ink-primary">
                          {fmt$(creditInfo.creditLimit)}
                        </span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-ink-muted">Current Utilization</span>
                        <span className="font-mono text-ink-primary">
                          {fmtPct(
                            creditInfo.creditLimit > 0
                              ? ((creditInfo.creditLimit - creditInfo.available) /
                                  creditInfo.creditLimit) *
                                  100
                              : 0
                          )}
                        </span>
                      </div>
                      <div className="flex justify-between text-xs pt-1 border-t border-border-subtle">
                        <span className="text-ink-muted">Projected After Approve</span>
                        <span className={clsx('font-mono font-medium', creditColor)}>
                          {fmtPct(
                            creditInfo.creditLimit > 0
                              ? ((creditInfo.creditLimit -
                                  creditInfo.available +
                                  summary.totalCost) /
                                  creditInfo.creditLimit) *
                                  100
                              : 0
                          )}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modals — Day 64.5 V-ROUI: don't mount for viewer (no trigger button
          shows; this is belt-and-suspenders against URL-driven modal open). */}
      {!isViewer && (
        <>
          <AddProductsModal
            open={showAddModal}
            onClose={() => setShowAddModal(false)}
            onAdd={handleAddProduct}
            existingProductIds={existingProductIds}
          />
          <ConfirmModal
            open={showConfirmModal}
            poNumber={poNumber}
            totalSkus={summary.totalSkus}
            totalUnits={summary.totalUnits}
            totalCost={summary.totalCost}
            estProfit={summary.estTotalProfit}
            maxSpend={resolvedConstraints.maxSpend}
            onConfirm={handleConfirmSubmit}
            onCancel={() => setShowConfirmModal(false)}
            submitting={builder.submitting}
            unreviewedLines={unreviewedLines}
            unreviewedSkuList={unreviewedSkuList}
            onAckMatches={handleAckMatches}
            acking={acking}
          />
        </>
      )}
    </div>
  );
}
